import classNames from 'classnames';
import { MouseEventHandler, ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { Button, attempt } from 'yoso';
import { useIsMobile } from '~/components/Breakpoint/Breakpoint.hook';
import { useCelect } from '~/components/Form/Celect/Celect.hooks';
import { Close } from '~/components/Icon/Close';
import { useBodyScrollLock } from '~/components/Scroll/Scoll.hooks';
import { useUpdateLayoutEffect } from '~/components/Util/react';
import styles from './CelectModal.module.scss';

export type CelectModalProps = {
  className?: string;
  children?: ReactNode;
  closeButton?: ReactNode;
  onCloseButtonClick?: MouseEventHandler;
};
export const CelectModal = ({
  className,
  children,
  closeButton,
  onCloseButtonClick,
}: CelectModalProps) => {
  const context = useCelect();
  const { active, closeOnClick, close, placement } = context;
  const isMobile = useIsMobile();
  const [playing, setPlaying] = useState<boolean | null>(false);
  const scrolled = useRef<boolean>(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const onTransitionEnd = useCallback(() => {
    setPlaying(false);
  }, []);

  useEffect(() => {
    if (!active && scrolled.current) scrolled.current = false;
  }, [active]);

  useEffect(() => {
    if (!active) return;
    const el = rootRef.current?.querySelector('[aria-selected="true"]');
    if (el && !scrolled.current) {
      el.scrollIntoView({ block: 'nearest' });
      scrolled.current = true;
    }
  }, [active, children]);

  useUpdateLayoutEffect(() => {
    setPlaying(null);
    requestAnimationFrame(() => {
      setPlaying(true);
    });
  }, [active]);

  useBodyScrollLock(isMobile && active);

  const onBackgroundClick = useCallback(
    (event) => {
      event.stopPropagation();
      close();
    },
    [close]
  );

  return (
    <div
      className={classNames(
        styles.root,
        styles[placement],
        {
          [styles.open]: active,
          [styles.close]: !active,
          [styles.playing]: playing === true,
          [styles.done]: playing === false,
        },
        className
      )}
      ref={rootRef}
      onTransitionEnd={onTransitionEnd}>
      <div className={styles.dimmed} onClick={onBackgroundClick} />
      <div className={styles.inner}>
        {closeButton === undefined ? (
          <Button
            className={styles.button}
            variant="transparent"
            size="small"
            onClick={onCloseButtonClick || context.close}
            aria-label="close">
            <Close />
          </Button>
        ) : (
          closeButton
        )}
        <div className={classNames(styles.content, 'body-scroll-lock-ignore')} aria-label="content">
          {attempt(children, context)}
        </div>
      </div>
    </div>
  );
};
